import { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, Tags as TagsIcon } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { Tag, ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import Modal from '../components/ui/Modal';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmDialog';

export default function TagsPage() {
  const { fetchApi, isAdmin } = useApi();
  const toast = useToast();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const confirm = useConfirm();

  useEffect(() => {
    loadTags();
  }, []);

  async function loadTags() {
    try {
      const res = await fetchApi<ApiResponse<(Tag & { domain_count: number })[]>>('/tags');
      if (res.data) setTags(res.data);
    } catch (error) {
      console.error('Failed to load tags:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async (id: string) => {
    if (!(await confirm({ title: 'Delete tag?', message: 'Are you sure you want to delete this tag?', tone: 'danger' }))) return;

    try {
      await fetchApi(`/tags/${id}`, { method: 'DELETE' });
      loadTags();
    } catch (error) {
      toast.error('Failed to delete tag');
    }
  };

  const handleEdit = (tag: Tag) => {
    setEditingTag(tag);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTag(null);
    setShowModal(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tags"
        actions={
          <button onClick={handleAdd} className="btn btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Add Tag
          </button>
        }
      />

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }, (_, i) => <SkeletonCard key={i} lines={2} />)}
        </div>
      ) : tags.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={TagsIcon}
            title="No tags yet"
            action={
              <button onClick={handleAdd} className="btn btn-primary">
                Create your first tag
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tags.map((tag) => (
            <div key={tag.id} className="card p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  />
                  <div>
                    <h3 className="font-semibold text-slate-900">{tag.name}</h3>
                    <p className="text-sm text-slate-500">
                      {(tag as Tag & { domain_count?: number }).domain_count || 0} domains
                    </p>
                  </div>
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="p-1 text-slate-400 hover:text-brand-600"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="p-1 text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
              {tag.description && (
                <p className="mt-2 text-sm text-slate-600">{tag.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TagModal
          tag={editingTag}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadTags();
          }}
        />
      )}
    </div>
  );
}

function TagModal({
  tag,
  onClose,
  onSuccess,
}: {
  tag: Tag | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { fetchApi } = useApi();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(tag?.name || '');
  const [color, setColor] = useState(tag?.color || '#6366f1');
  const [description, setDescription] = useState(tag?.description || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (tag) {
        await fetchApi(`/tags/${tag.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name, color, description }),
        });
      } else {
        await fetchApi('/tags', {
          method: 'POST',
          body: JSON.stringify({ name, color, description }),
        });
      }
      onSuccess();
    } catch (error) {
      toast.error(`Failed to ${tag ? 'update' : 'create'} tag`, error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const presetColors = [
    '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e',
    '#14b8a6', '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6',
    '#a855f7', '#d946ef', '#ec4899', '#f43f5e',
  ];

  return (
    <Modal
      title={tag ? 'Edit Tag' : 'Create Tag'}
      onClose={onClose}
      dismissible={!loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" form="tag-form" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : tag ? 'Update Tag' : 'Create Tag'}
          </button>
        </>
      }
    >
        <form id="tag-form" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="label">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production"
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">Color</label>
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg border border-slate-200"
                  style={{ backgroundColor: color }}
                />
                <input
                  type="text"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="input w-28 font-mono"
                  pattern="^#[0-9A-Fa-f]{6}$"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {presetColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-full ${
                      color === c ? 'ring-2 ring-offset-2 ring-brand-500' : ''
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="description" className="label">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description..."
                rows={2}
                className="input"
              />
            </div>
          </div>
        </form>
    </Modal>
  );
}
